import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import {
  I18nProvider as EcloudI18nProvider,
  Text,
  TradeLabel,
  type TradeLabelProps,
} from '@enechain/ecloud-designsystem'

/**
 * `as` / `display` を通せるようにした `TradeLabel`。
 *
 * DS `TradeLabel` は中身が chakra `Text` で、受け取った残り props を最後に
 * そのまま spread するため `as` / `display` は実行時には効く。 一方で公開型は
 * `{ pattern } & React.HTMLAttributes<HTMLParagraphElement>` に固定されていて
 * chakra の polymorphic prop が落ちているため、 型だけここで広げる
 * (DS 側で `TextProps` を露出してくれれば不要になる)。
 */
const PolymorphicTradeLabel = TradeLabel as unknown as React.FC<
  TradeLabelProps & { as?: 'span'; display?: 'block' | 'inline-block' }
>

type Props = {
  side: Side
  /**
   * phrasing content しか置けない親 (`<span>` / `<label>` 等) の中で使う時に
   * `'span'` を渡す。 既定 (`undefined`) は DS のまま `<p>`。
   *
   * DS `TradeLabel` は `<p>` (= flow content) を描くため、 例えば DS
   * `RadioGroupItemText` (= `<span>`) の中に素で置くと
   * `<label><span><p/></span></label>` という不正な入れ子になる
   * (`TradeSideRadioGroup` が該当)。
   */
  as?: 'span'
}

/**
 * DS 内蔵 i18n の Locale 型は 'ja' | 'en' | undefined。undefined を渡すと DS が
 * props をそのまま返しラベルが空になるため、未知の言語コードは 'ja' に
 * フォールバックさせる (= undefined を渡さない)。
 *
 * このアプリの i18next 設定は `supportedLngs: ['ja','en']` で
 * `changeLanguage` に未知コードを渡しても `i18n.language` 自体が 'ja' に
 * 正規化されるため、`TradeSideLabel` の render 経由ではこの関数の
 * fallback 分岐に未知コードを到達させられない。テストで直接検証できるよう
 * named export にしている (= コンポーネント外の呼び出し元は無い)。
 */
export function resolveLocale(language: string): 'ja' | 'en' {
  return language.startsWith('en') ? 'en' : 'ja'
}

/**
 * 売買方向 (`Side`) を DS `TradeLabel` の色付き pill
 * (`Object.bid` / `Object.offer`) で表示する共通ラッパ (design来歴は
 * `docs/adr/trade-side-label-ds-authority.md` を参照)。
 *
 * i18n の二系統 (意図的な分離):
 *   - 表示ラベルの SoT は DS 内蔵辞書 (ecloud `I18nProvider`)。DS は
 *     ja「買」「売」/ en "B"/"O" の 1 文字表示のみを持つ。
 *   - 読み上げ用 `aria-label` は ebalance の辞書
 *     (`common:tradeSide.buy`/`.sell`/`.unspecified`) から取る。DS は読み上げ用
 *     の語を持たないため、表示と読み上げで文言ソースが分かれる。SupplyBilateral
 *     / SpotBidding / IntradayBidding の 3 画面横断で使うため、ページ固有の
 *     `page.json` ではなく app 横断の `common.json` を引く
 *     (`frontend/apps/ebalance/CLAUDE.md` の i18n key 規約準拠)。
 *
 * アクセシビリティ:
 *   - en 表示が 1 文字 ("B"/"O") になるため、design プロトタイプの
 *     `font-size:0` + `::before` hack (textContent に語を残す) に相当する
 *     情報を `aria-label` で担保する (必須)。
 *   - DS `TradeLabel` は `<p>` を描画し、明示 role が無いと暗黙 ARIA role が
 *     `paragraph` になる。`paragraph` は ARIA の naming prohibited role
 *     (`generic`/`caption`/`emphasis`/`strong` 等と同群) のため `aria-label`
 *     を付与しても accessible name として無効化される (axe-core
 *     `aria-prohibited-attr` の検出対象)。`role="img"` (naming supported、
 *     children を name 計算から除外) を明示付与して `aria-label` を
 *     実効化する。visually-hidden なテキストノード併置案は `TradeLabel` が
 *     children を受け付けず外側にラッパ要素が要る (table cell / RadioGroup
 *     内に余分な DOM が増える) ため、1 属性で完結する `role="img"` を採用した。
 *
 * 規約からの逸脱: ecloud `I18nProvider` は本来 app root で 1 回配線するのが
 * DS の標準的な使い方だが、app 全体配線 (EN セッションで DatePicker 等の
 * DS 内蔵文言が日本語のままになる既存不具合の解消) は別 slice の責務
 * (design スコープ「含まない」) のため、本コンポーネント内に局所的に張る。
 * 局所化により影響は売買 pill に閉じる (AGENTS.md「規約からの逸脱」対応)。
 */
export const TradeSideLabel = ({ side, as }: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // useMemo は使わない (設計書 §4 で撤回): ecloud `I18nProvider` は内部で
  // `value={{ locale }}` という object literal を毎 render 生成する実装のため
  // context value の同一性は app 側から固定できず、resolveLocale の戻り値も
  // string で memo 化は値比較上 no-op。`frontend/apps/ebalance/CLAUDE.md`
  // 「細粒度 memoization は計測してから入れる、デフォルト不要」に従う。
  const locale = resolveLocale(i18n.language)
  const isBuy = side === Side.BUY
  const isSell = side === Side.SELL

  if (!isBuy && !isSell) {
    // UNSPECIFIED (および想定外値): DS `TradeLabel` に渡すと背景 transparent・
    // 文字なしの空箱になり、データ異常が画面上で不可視になるため、ラッパ側で
    // 分岐して中間色テキストで fallback する (`TradeLabel` には渡さない)。
    return (
      <Text as="span" color="Text.midEmphasis">
        {t('common:tradeSide.unspecified')}
      </Text>
    )
  }

  const pattern: 'bid' | 'offer' = isBuy ? 'bid' : 'offer'
  const ariaLabel = isBuy
    ? t('common:tradeSide.buy')
    : t('common:tradeSide.sell')

  return (
    <EcloudI18nProvider locale={locale}>
      {/* role="img": DS TradeLabel が描画する <p> の暗黙 role "paragraph" は
          naming prohibited のため、aria-label を実効化するために明示する
          (JSDoc 参照)。span 化時に display="block" を添えるのは、 span の既定
          display (inline) では DS が指定する width/height 20px が効かず pill が
          潰れるため (= <p> と同じブロックボックスに戻す)。

          既定 (<p>) 側に display="inline-block" を添えるのは、 DS が指定する
          width 20px と相まって <p> が**ブロックボックス**になり、 table cell の
          text-align: center が効かない (= 中央寄せ指定済みのセルでも pill が左端に
          貼り付く) ため (2026-09-01 デザイナー指摘「売/買 カラムの値をセンター揃えに」)。
          8080 の `.ecl-trade-label` も inline-flex = インラインレベルで、 セル側の
          text-align で中央に寄る前提の作り (css/components.css `.ecl-trade-label`)。
          inline-flex ではなく inline-block を採るのは、 DS が pill 内部の文字寄せに
          textAlign: "center" を使っており、 flex 化するとその指定が無効化されて
          グリフが左に寄るため (DS は justify-content を持たない)。*/}
      <PolymorphicTradeLabel
        as={as}
        display={as === 'span' ? 'block' : 'inline-block'}
        aria-label={ariaLabel}
        pattern={pattern}
        role="img"
      />
    </EcloudI18nProvider>
  )
}
